import { useState, useCallback, useMemo, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';
import { ApplicationDataLeadsQuery } from '../queries';
import { FunctionalArea } from '../types';
import { RoleId } from '../../../insights/types/RoleTypes';

export interface UseApplicationRefreshReturn {
  isRefreshing: boolean;
  refreshSpecificApplications: (applicationIds: number[]) => Promise<void>;
  refreshApplicationData: (applicationId: number) => Promise<void>;
}

interface UseApplicationRefreshProps {
  selectedFunctionalArea: string;
  setCompleteFunctionalAreasData: (
    data: FunctionalArea[] | ((prev: FunctionalArea[]) => FunctionalArea[]),
  ) => void;
  dataLeadManager: {
    clearPendingChanges: (applicationIds: number[]) => void;
  };
}

/**
 * Custom hook to manage application refresh logic
 * @param param0 - Props for the hook
 * @returns Application refresh state and handlers
 */
export const useApplicationRefresh = ({
  selectedFunctionalArea,
  setCompleteFunctionalAreasData,
  dataLeadManager,
}: UseApplicationRefreshProps): UseApplicationRefreshReturn => {
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const isRefreshingRef = useRef<boolean>(false);

  const [fetchApplicationDataLeads] = useLazyQuery(ApplicationDataLeadsQuery, {
    errorPolicy: 'all',
    fetchPolicy: 'network-only', // Force fresh network request, bypass cache
    notifyOnNetworkStatusChange: true,
  });

  // Function to refresh specific applications by their IDs
  const refreshSpecificApplications = useCallback(
    async (applicationIds: number[]) => {
      const startTimestamp = new Date().toISOString();
      // eslint-disable-next-line no-console -- High-level debugging for refresh timing operations in development
      console.log(
        `🔄 [${startTimestamp}] refreshSpecificApplications called for applications:`,
        applicationIds,
      );

      if (!selectedFunctionalArea || applicationIds.length === 0) {
        // eslint-disable-next-line no-console -- High-level debugging for refresh validation timing in development
        console.log(
          `⏹️ [${new Date().toISOString()}] Refresh aborted - no functional area or applications`,
        );
        return;
      }

      // Prevent multiple simultaneous refreshes
      if (isRefreshingRef.current) {
        // eslint-disable-next-line no-console -- High-level debugging for refresh collision timing in development
        console.log(
          `⏸️ [${new Date().toISOString()}] Refresh already in progress, skipping duplicate call`,
        );
        return;
      }

      try {
        isRefreshingRef.current = true;
        setIsRefreshing(true);
        // eslint-disable-next-line no-console -- High-level debugging for refresh state timing in development
        console.log(
          `🟡 [${new Date().toISOString()}] Refresh state set to TRUE - circular progress should appear`,
        );

        // Fetch updated data for each application
        const applicationUpdates = await Promise.all(
          applicationIds.map(async applicationId => {
            // eslint-disable-next-line no-console -- High-level debugging for GraphQL operations in development
            console.log(
              '📞 Making GraphQL query for application:',
              applicationId,
            );
            const result = await fetchApplicationDataLeads({
              variables: {
                applicationId,
                filter: {
                  roleId: RoleId.DataLead,
                },
              },
            });
            return {
              applicationId,
              applicationData: result.data?.resources?.application,
            };
          }),
        );

        // eslint-disable-next-line no-console -- High-level debugging for application refresh operations in development
        console.log('📋 Application updates received:', applicationUpdates);

        // Update the functional area data with the fresh application data
        // eslint-disable-next-line no-console -- High-level debugging for functional area data updates in development
        console.log('🔄 Updating functional area data...');
        setCompleteFunctionalAreasData(prev => {
          // eslint-disable-next-line no-console -- High-level debugging for functional area data state in development
          console.log('📊 Previous functional area data:', prev);
          return prev.map(functionalArea => {
            if (functionalArea.name === selectedFunctionalArea) {
              // eslint-disable-next-line no-console -- High-level debugging for functional area matching in development
              console.log(
                '🎯 Found matching functional area:',
                functionalArea.name,
              );
              const updatedApplications = functionalArea.applications.map(
                app => {
                  const update = applicationUpdates.find(
                    u => u.applicationId === app.applicationId,
                  );
                  return update?.applicationData || app;
                },
              );

              // eslint-disable-next-line no-console -- High-level debugging for application updates in development
              console.log(
                '📝 Updated applications for functional area:',
                updatedApplications,
              );
              return {
                ...functionalArea,
                applications: updatedApplications,
              };
            }
            return functionalArea;
          });
        });
        // eslint-disable-next-line no-console -- High-level debugging for functional area data completion in development
        console.log('✅ Functional area data updated');
      } catch (error) {
        // eslint-disable-next-line no-console -- Error logging for refresh operations debugging in development
        console.error('❌ Error refreshing specific applications:', error);
      } finally {
        isRefreshingRef.current = false;
        setIsRefreshing(false);
        const endTimestamp = new Date().toISOString();
        // eslint-disable-next-line no-console -- High-level debugging for refresh completion timing in development
        console.log(
          `🏁 [${endTimestamp}] refreshSpecificApplications completed - circular progress should disappear`,
        );
      }
    },
    [
      selectedFunctionalArea,
      fetchApplicationDataLeads,
      setCompleteFunctionalAreasData,
    ],
  );

  // Function to refresh data for a specific application (for real-time updates)
  const refreshApplicationData = useCallback(
    async (applicationId: number) => {
      // eslint-disable-next-line no-console -- High-level debugging for application data refresh operations in development
      console.log(
        '🎯 refreshApplicationData called for application:',
        applicationId,
      );

      if (!selectedFunctionalArea) {
        // eslint-disable-next-line no-console -- High-level debugging for refresh validation in development
        console.log('❌ No selectedFunctionalArea, skipping refresh');
        return;
      }

      try {
        // eslint-disable-next-line no-console -- High-level debugging for refresh operations in development
        console.log(
          '🔄 Calling refreshSpecificApplications for:',
          applicationId,
        );
        await refreshSpecificApplications([applicationId]);
        // eslint-disable-next-line no-console -- High-level debugging for refresh completion in development
        console.log(
          '✅ refreshSpecificApplications completed for:',
          applicationId,
        );

        // eslint-disable-next-line no-console -- High-level debugging for pending changes cleanup in development
        console.log(
          '🧹 Now clearing pending changes for application:',
          applicationId,
        );
        dataLeadManager.clearPendingChanges([applicationId]);
        // eslint-disable-next-line no-console -- High-level debugging for cleanup completion in development
        console.log('✅ Pending changes cleared for:', applicationId);
      } catch (error) {
        // eslint-disable-next-line no-console -- Error logging for application refresh debugging in development
        console.error('❌ Error refreshing application data:', error);
      }
    },
    [selectedFunctionalArea, refreshSpecificApplications, dataLeadManager],
  );

  return useMemo(
    () => ({
      isRefreshing,
      refreshSpecificApplications,
      refreshApplicationData,
    }),
    [isRefreshing, refreshSpecificApplications, refreshApplicationData],
  );
};
